import "@/components/brands/brand-content.css";
import { useChatPlatformNavigation } from './canvas/chat-platform-navigation';
import { CompetitorProfileEditor } from './CompetitorProfileEditor';
import { flattenBrandCompetitors } from './brand-competitors';
import { SaveTemplateBrandMenu } from '@/components/template-brand-menu';
import { Layout } from "@/components/layout";
import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  ArrowLeft,
  Crosshair,
  Loader2,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Lightbulb,
  MessageSquare,
  Target,
  Shield,
  TrendingDown,
  TrendingUp,
  Globe,
  Sparkles,
  Search,
  Facebook,
  Play,
  ExternalLink,
  BookmarkPlus,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { CompetitorBoard, CompetitorProfile, CompetitorSentiment, MarketingAngle, ResearchJob, Brand } from "@shared/schema";
import Masonry from "react-masonry-css";

interface DiscoveredSuggestion {
  brandName: string;
  description: string;
  targetAudience: string;
  positioning: string;
  logoUrl: string;
}

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    pending: { label: "Pending", className: "bg-[#F0EFED] text-[#8C8C8A]", icon: <Clock className="h-3 w-3" /> },
    analyzing: { label: "Analyzing", className: "bg-[#F0EFED] text-[#8C8C8A]", icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    processing: { label: "Processing", className: "bg-[#F0EFED] text-[#8C8C8A]", icon: <Loader2 className="h-3 w-3 animate-spin" /> },
    completed: { label: "Completed", className: "bg-[#E8F5EA] text-[#4CAF50]", icon: <CheckCircle2 className="h-3 w-3" /> },
    failed: { label: "Failed", className: "bg-red-50 text-red-500", icon: <AlertCircle className="h-3 w-3" /> },
  };
  const c = config[status] || config.pending;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.className}`}>
      {c.icon} {c.label}
    </span>
  );
}

function DiscoverDialog({
  boardId,
  open,
  onOpenChange,
  trigger,
}: {
  boardId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [suggestions, setSuggestions] = useState<DiscoveredSuggestion[]>([]);
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set());

  const discoverMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/competitor-boards/${boardId}/discover`, { method: "POST" });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json() as Promise<{ suggestions: DiscoveredSuggestion[] }>;
    },
    onSuccess: (data) => {
      setSuggestions(data.suggestions);
      setSelectedNames(new Set(data.suggestions.map((s) => s.brandName)));
    },
    onError: (err: Error) => {
      toast({ title: "Discovery failed", description: err.message, variant: "destructive" });
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      const selected = suggestions.filter((s) => selectedNames.has(s.brandName));
      const res = await fetch(`/api/competitor-boards/${boardId}/analyze-selected`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ competitors: selected }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/competitor-boards/${boardId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/competitor-boards/${boardId}/competitors`] });
      onOpenChange(false);
      setSuggestions([]);
      setSelectedNames(new Set());
      toast({ title: "Analysis started", description: `Analyzing ${selectedNames.size} competitors in the background.` });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  useEffect(() => {
    if (open && suggestions.length === 0 && !discoverMutation.isPending) {
      discoverMutation.mutate();
    }
    if (!open) {
      setSuggestions([]);
      setSelectedNames(new Set());
    }
  }, [open]);

  const toggleSelection = (name: string) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Discover Competitors</DialogTitle>
          <DialogDescription>
            AI found these competitors. Select which ones to run full analysis on.
          </DialogDescription>
        </DialogHeader>

        {discoverMutation.isPending ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="h-8 w-8 rounded-full border-2 border-[#E5E5E3] border-t-[#8C8C8A] animate-spin mb-3" />
            <p className="text-sm text-[#6B6B6B]">Discovering competitors...</p>
            <p className="text-xs text-[#8C8C8A] mt-1">This usually takes 10-15 seconds</p>
          </div>
        ) : suggestions.length > 0 ? (
          <div className="space-y-4 mt-2">
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {suggestions.map((s) => {
                const isSelected = selectedNames.has(s.brandName);
                return (
                  <div
                    key={s.brandName}
                    data-testid={`suggestion-${s.brandName}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      isSelected ? "border-[#1A1A1A] bg-[#F8F8F7]" : "border-[#E5E5E3] hover:border-[#8C8C8A]"
                    }`}
                    onClick={() => toggleSelection(s.brandName)}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelection(s.brandName)}
                      className="mt-1"
                    />
                    <img
                      src={s.logoUrl}
                      alt={s.brandName}
                      className="h-8 w-8 rounded-full bg-gray-100 object-contain shrink-0 mt-0.5"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect fill="#E5E7EB" width="40" height="40" rx="20"/><text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle" fill="#6B7280" font-size="16" font-family="system-ui">${s.brandName.charAt(0)}</text></svg>`)}`;
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900">{s.brandName}</h4>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{s.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-xs text-gray-400">{selectedNames.size} of {suggestions.length} selected</span>
              <div className="flex gap-2">
                <Button
                  data-testid="button-rediscover"
                  variant="outline"
                  size="sm"
                  disabled={discoverMutation.isPending}
                  onClick={() => {
                    setSuggestions([]);
                    setSelectedNames(new Set());
                    discoverMutation.mutate();
                  }}
                >
                  <Search className="h-3.5 w-3.5 mr-1.5" />
                  Find Different
                </Button>
                <Button
                  data-testid="button-analyze-selected"
                  size="sm"
                  className="bg-[#1A1A1A] hover:bg-[#2a2a2a] text-white"
                  disabled={selectedNames.size === 0 || analyzeMutation.isPending}
                  onClick={() => analyzeMutation.mutate()}
                >
                  {analyzeMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Crosshair className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Analyze Selected ({selectedNames.size})
                </Button>
              </div>
            </div>
          </div>
        ) : discoverMutation.isError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-8 w-8 text-red-400 mb-3" />
            <p className="text-sm text-gray-500">Failed to discover competitors</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => discoverMutation.mutate()}>
              Try Again
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function BoardsListing({ onSelectBoard, hideHeader, brandId: filterBrandId }: { onSelectBoard?: (boardId: number) => void; hideHeader?: boolean; brandId?: number }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [boardName, setBoardName] = useState("");
  const [selectedBrandId, setSelectedBrandId] = useState<string>(filterBrandId ? String(filterBrandId) : "");

  const { data: boards = [], isLoading } = useQuery<CompetitorBoard[]>({
    queryKey: filterBrandId ? ["/api/competitor-boards", { brandId: filterBrandId }] : ["/api/competitor-boards"],
    queryFn: async () => {
      const url = filterBrandId ? `/api/competitor-boards?brandId=${filterBrandId}` : "/api/competitor-boards";
      const res = await fetch(url);
      return res.json();
    },
  });

  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ["/api/brands"],
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; brandId: number }) => {
      const res = await fetch("/api/competitor-boards", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json();
    },
    onSuccess: (board: CompetitorBoard) => {
      queryClient.invalidateQueries({ queryKey: ["/api/competitor-boards"] });
      if (filterBrandId) {
        queryClient.invalidateQueries({ queryKey: ["/api/competitor-boards", { brandId: filterBrandId }] });
      }
      setCreateOpen(false);
      setBoardName("");
      if (!filterBrandId) setSelectedBrandId("");
      if (onSelectBoard) {
        onSelectBoard(board.id);
      } else {
        setLocation(`/competitor-analysis/${board.id}`);
      }
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/competitor-boards/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/competitor-boards"] });
      if (filterBrandId) {
        queryClient.invalidateQueries({ queryKey: ["/api/competitor-boards", { brandId: filterBrandId }] });
      }
      toast({ title: "Board deleted" });
    },
  });

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
        {!hideHeader && (
          <div>
            <h1 data-testid="text-page-title" className="text-xl font-semibold text-gray-900">Competitor Analysis</h1>
            <p className="text-sm text-gray-500 mt-0.5">Research your market landscape and extract actionable ad insights</p>
          </div>
        )}
        {hideHeader && <div />}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-new-board" className="bg-[#1A1A1A] hover:bg-[#2a2a2a] text-white">
              <Plus className="h-4 w-4 mr-2" /> New Analysis
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Competitor Analysis</DialogTitle>
              <DialogDescription>Create a new analysis board to discover and research competitors.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Analysis Name</label>
                <Input
                  data-testid="input-board-name"
                  placeholder="e.g. Q1 Competitive Landscape"
                  value={boardName}
                  onChange={(e) => setBoardName(e.target.value)}
                />
              </div>
              {!filterBrandId && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Select Brand</label>
                  <Select value={selectedBrandId} onValueChange={setSelectedBrandId}>
                    <SelectTrigger data-testid="select-brand">
                      <SelectValue placeholder="Choose a brand to analyze against" />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                data-testid="button-create-board"
                className="w-full bg-[#1A1A1A] hover:bg-[#2a2a2a] text-white"
                disabled={!boardName.trim() || !selectedBrandId || createMutation.isPending}
                onClick={() => createMutation.mutate({ name: boardName.trim(), brandId: Number(selectedBrandId) })}
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Crosshair className="h-4 w-4 mr-2" />}
                Create Board
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 rounded-full border-2 border-[#E5E5E3] border-t-[#8C8C8A] animate-spin" />
          </div>
        ) : boards.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Crosshair className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No analyses yet</h3>
            <p className="text-sm text-gray-400 mt-1 max-w-md">
              Create your first competitor analysis to discover market insights and ad angles.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {boards.map((board) => {
              const brand = brands.find((b) => b.id === board.brandId);
              return (
                <Card
                  key={board.id}
                  data-testid={`card-board-${board.id}`}
                  className="cursor-pointer hover:shadow-md transition-shadow group relative"
                  onClick={() => onSelectBoard ? onSelectBoard(board.id) : setLocation(`/competitor-analysis/${board.id}`)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {brand?.logo && (
                          <img src={brand.logo} alt={brand.name} className="h-8 w-8 rounded-full object-cover" />
                        )}
                        <div>
                          <h3 className="font-semibold text-gray-900">{board.name}</h3>
                          <p className="text-xs text-gray-500">{brand?.name || "Unknown brand"}</p>
                        </div>
                      </div>
                      <StatusBadge status={board.status} />
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-xs text-gray-400">
                        {new Date(board.createdAt).toLocaleDateString()}
                      </span>
                      <Button
                        data-testid={`button-delete-board-${board.id}`}
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMutation.mutate(board.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SourceIndicator({ jobs }: { jobs: ResearchJob[] }) {
  return (
    <div className="flex gap-3">
      {jobs.map((job, idx) => (
        <span key={`${job.id}-${idx}`} className="text-xs text-gray-500 flex items-center gap-1">
          {job.status === "completed" ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
          ) : job.status === "failed" ? (
            <AlertCircle className="h-3.5 w-3.5 text-red-400" />
          ) : job.status === "processing" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
          ) : (
            <Clock className="h-3.5 w-3.5 text-gray-300" />
          )}
          {job.sourceModule}
        </span>
      ))}
    </div>
  );
}

function SkeletonLine({ className = "" }: { className?: string }) {
  return (
    <div
      className={`rounded animate-shimmer bg-gradient-to-r from-[#F8F8F7] via-[#E5E5E3] to-[#F8F8F7] bg-[length:400%_100%] ${className}`}
      style={{ backgroundSize: "400% 100%" }}
    />
  );
}

export function CompetitorCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-[#E5E5E3] p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-full flex-shrink-0 animate-shimmer bg-gradient-to-r from-[#F8F8F7] via-[#E5E5E3] to-[#F8F8F7]" style={{ backgroundSize: "400% 100%" }} />
        <div className="flex-1 min-w-0 space-y-1.5">
          <SkeletonLine className="h-3.5 w-2/3" />
          <SkeletonLine className="h-2.5 w-1/3" />
        </div>
      </div>
      <SkeletonLine className="h-2.5 w-full mb-1.5" />
      <SkeletonLine className="h-2.5 w-4/5 mb-3" />
      <div className="flex gap-1.5 mt-2">
        <SkeletonLine className="h-5 w-16 rounded-full" />
        <SkeletonLine className="h-5 w-20 rounded-full" />
      </div>
    </div>
  );
}

function ProfileTabSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i}>
          <SkeletonLine className="h-2.5 w-28 mb-2" />
          <SkeletonLine className="h-3 w-full mb-1" />
          <SkeletonLine className="h-3 w-4/5" />
        </div>
      ))}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2].map((i) => (
          <div key={i} className="space-y-2">
            <SkeletonLine className="h-2.5 w-24 mb-2" />
            {[1, 2, 3].map((j) => (
              <div key={j} className="flex items-start gap-2">
                <SkeletonLine className="h-2.5 w-2.5 rounded-full mt-0.5 flex-shrink-0" />
                <SkeletonLine className="h-2.5 flex-1" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SentimentTabSkeleton() {
  return (
    <div className="space-y-8">
      {[1, 2].map((i) => (
        <div key={i} className="space-y-5">
          <SkeletonLine className="h-5 w-24 rounded-full" />
          {[1, 2].map((j) => (
            <div key={j}>
              <SkeletonLine className="h-2.5 w-28 mb-3" />
              <div className="space-y-2">
                {[1, 2, 3].map((k) => (
                  <SkeletonLine key={k} className="h-3 w-full pl-4 border-l-2 border-[#E5E5E3]" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function AnglesTabSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-[#F8F8F7] rounded-lg border border-[#E5E5E3] p-4 space-y-2">
          <SkeletonLine className="h-3.5 w-3/4" />
          <SkeletonLine className="h-2.5 w-full" />
          <SkeletonLine className="h-2.5 w-2/3" />
        </div>
      ))}
    </div>
  );
}

function FacebookAdsTabSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-xl border border-[#E5E5E3] overflow-hidden">
          <SkeletonLine className="h-40 w-full rounded-none" />
          <div className="p-3 space-y-2">
            <SkeletonLine className="h-3 w-full" />
            <SkeletonLine className="h-3 w-4/5" />
            <SkeletonLine className="h-2.5 w-1/2 mt-1" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ProfileTab({ profile }: { profile: CompetitorProfile }) {
  return (
    <div className="space-y-6">
      {profile.description && (
        <div>
          <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-2">Description</h4>
          <p className="text-sm text-[#1A1A1A] leading-relaxed">{profile.description}</p>
        </div>
      )}
      {profile.targetAudience && (
        <div>
          <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-2">Target Audience</h4>
          <p className="text-sm text-[#1A1A1A] leading-relaxed">{profile.targetAudience}</p>
        </div>
      )}
      {profile.positioning && (
        <div>
          <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-2">Positioning</h4>
          <p className="text-sm text-[#1A1A1A] leading-relaxed">{profile.positioning}</p>
        </div>
      )}
      {profile.pricePositioning && (
        <div>
          <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-2">Price Positioning</h4>
          <p className="text-sm text-[#1A1A1A] leading-relaxed">{profile.pricePositioning}</p>
        </div>
      )}
      {profile.keyDifferentiators && (
        <div>
          <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-2">Key Differentiators</h4>
          <p className="text-sm text-[#1A1A1A] leading-relaxed">{profile.keyDifferentiators}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {profile.strengths && profile.strengths.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5 text-[#4CAF50]" /> Strengths
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {profile.strengths.map((s, i) => (
                <span key={`str-${i}`} className="px-2.5 py-1 rounded-full bg-[#F0FAF0] border border-[#C8E6C9] text-xs text-[#388E3C]">
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
        {profile.weaknesses && profile.weaknesses.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <TrendingDown className="h-3.5 w-3.5 text-red-400" /> Weaknesses
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {profile.weaknesses.map((w, i) => (
                <span key={`weak-${i}`} className="px-2.5 py-1 rounded-full bg-[#FFF5F5] border border-[#FFCDD2] text-xs text-[#C62828]">
                  {w}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {profile.marketingChannels && profile.marketingChannels.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-3">Marketing Channels</h4>
          <div className="flex flex-wrap gap-2">
            {profile.marketingChannels.map((ch, i) => (
              <span key={`ch-${i}`} className="px-2.5 py-1 rounded-full bg-[#F8F8F7] border border-[#E5E5E3] text-xs text-[#6B6B6B]">
                {ch}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SentimentTab({ profileId, status }: { profileId: number; status: string }) {
  const { data: sentiment = [], isLoading } = useQuery<CompetitorSentiment[]>({
    queryKey: [`/api/competitors/${profileId}/sentiment`],
    enabled: status === "completed",
  });

  if (isLoading) return <SentimentTabSkeleton />;

  if (sentiment.length === 0) {
    return (
      <p className="text-sm text-[#8C8C8A] text-center py-12">
        {status === "completed" ? "No sentiment data collected" : "Sentiment data will appear here once research completes"}
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {sentiment.map((s, idx) => (
        <div key={`sent-${s.id}-${idx}`} className="space-y-5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F0EFED] text-[#8C8C8A] border border-[#E5E5E3] capitalize">
              {s.sourcePlatform}
            </span>
          </div>
          {s.complaints && s.complaints.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-3">Complaints</p>
              <div className="space-y-2">
                {s.complaints.map((c, i) => (
                  <blockquote key={`c-${i}`} className="text-sm text-[#6B6B6B] pl-4 border-l-2 border-[#FFCDD2] leading-relaxed italic">
                    {c}
                  </blockquote>
                ))}
              </div>
            </div>
          )}
          {s.positives && s.positives.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-3">Positives</p>
              <div className="space-y-2">
                {s.positives.map((p, i) => (
                  <blockquote key={`p-${i}`} className="text-sm text-[#6B6B6B] pl-4 border-l-2 border-[#C8E6C9] leading-relaxed italic">
                    {p}
                  </blockquote>
                ))}
              </div>
            </div>
          )}
          {s.themes && s.themes.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#8C8C8A] uppercase tracking-wider mb-3">Themes</p>
              <div className="flex flex-wrap gap-1.5">
                {s.themes.map((t, i) => (
                  <span key={`t-${i}`} className="px-2.5 py-1 rounded-full bg-[#F8F8F7] border border-[#E5E5E3] text-xs text-[#6B6B6B]">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AnglesTab({ profileId, status, brandId }: { profileId: number; status: string; brandId?: number }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [checkedAngles, setCheckedAngles] = useState<Set<number>>(new Set());

  const { data: angles = [], isLoading: anglesLoading } = useQuery<MarketingAngle[]>({
    queryKey: [`/api/competitors/${profileId}/angles`],
    enabled: status === "completed",
  });

  const toggleAngle = useMutation({
    mutationFn: async ({ id, selected }: { id: number; selected: boolean }) => {
      const res = await fetch(`/api/marketing-angles/${id}/select`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ selected }),
      });
      if (!res.ok) throw new Error("Failed to update");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/competitors/${profileId}/angles`] });
    },
  });

  const saveToBrandMutation = useMutation({
    mutationFn: async () => {
      const selectedAngles = angles.filter((a) => checkedAngles.has(a.id));
      const res = await fetch(`/api/brands/${brandId}/saved-angles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          angles: selectedAngles.map((a) => ({
            hookHeadline: a.hookHeadline,
            hookDescription: a.hookDescription,
            sourceType: a.sourceType,
          })),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message || "Failed to save angles");
      return res.json();
    },
    onSuccess: () => {
      const count = checkedAngles.size;
      setCheckedAngles(new Set());
      toast({ title: "Angles saved", description: `${count} angle${count !== 1 ? "s" : ""} saved to brand.` });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const toggleChecked = (id: number) => {
    setCheckedAngles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllChecked = () => {
    if (checkedAngles.size === angles.length) {
      setCheckedAngles(new Set());
    } else {
      setCheckedAngles(new Set(angles.map((a) => a.id)));
    }
  };

  if (anglesLoading) return <AnglesTabSkeleton />;

  if (angles.length === 0) {
    return (
      <p className="text-sm text-[#8C8C8A] text-center py-12">
        {status === "completed" ? "No marketing angles extracted" : "Ad angles will appear here once analysis completes"}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {brandId && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="checkbox-select-all-angles"
              checked={checkedAngles.size === angles.length && angles.length > 0}
              onCheckedChange={toggleAllChecked}
            />
            <span className="text-xs text-[#8C8C8A]">
              {checkedAngles.size > 0 ? `${checkedAngles.size} selected` : "Select all"}
            </span>
          </div>
          {checkedAngles.size > 0 && (
            <Button
              data-testid="button-save-to-brand"
              size="sm"
              className="bg-[#1A1A1A] hover:bg-[#2a2a2a] text-white"
              disabled={saveToBrandMutation.isPending}
              onClick={() => saveToBrandMutation.mutate()}
            >
              {saveToBrandMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Target className="h-3.5 w-3.5 mr-1.5" />
              )}
              Save to Brand ({checkedAngles.size})
            </Button>
          )}
        </div>
      )}
      {angles.map((angle) => (
        <div
          key={angle.id}
          data-testid={`card-angle-${angle.id}`}
          className={`border rounded-lg p-4 transition-colors ${angle.selected ? "border-[#1A1A1A] bg-[#F8F8F7]" : "border-[#E5E5E3] bg-white"}`}
        >
          <div className="flex items-start gap-3">
            {brandId && (
              <Checkbox
                data-testid={`checkbox-save-angle-${angle.id}`}
                checked={checkedAngles.has(angle.id)}
                onCheckedChange={() => toggleChecked(angle.id)}
                className="mt-0.5"
              />
            )}
            <Checkbox
              data-testid={`checkbox-angle-${angle.id}`}
              checked={angle.selected}
              onCheckedChange={(checked) =>
                toggleAngle.mutate({ id: angle.id, selected: !!checked })
              }
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-[#1A1A1A]">{angle.hookHeadline}</h4>
              {angle.hookDescription && (
                <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">{angle.hookDescription}</p>
              )}
              {angle.sourceType && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F0EFED] text-[#8C8C8A] border border-[#E5E5E3] mt-2 capitalize">
                  {angle.sourceType}
                </span>
              )}
            </div>
            <Lightbulb className="h-4 w-4 text-[#8C8C8A] shrink-0" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface FbAdResult {
  ad_id: string;
  page_name: string;
  page_id: string;
  page_logo_url: string;
  ad_creative_body: string;
  ad_creative_link_title: string;
  ad_creative_link_description: string;
  ad_creative_link_url: string;
  cta_type: string;
  media_type: "image" | "video" | "carousel";
  media_url: string;
  media_thumbnail_url: string;
  snapshot_url: string;
  platform: string;
  platforms: string[];
  status: string;
  ad_start_date: string;
  ad_end_date: string | null;
  ad_start_date_raw: string;
  ad_end_date_raw: string | null;
  languages: string[];
  search_country: string;
}

function fbFormatDaysAgo(startDate: string): string {
  if (!startDate) return "";
  try {
    const start = new Date(startDate);
    const now = new Date();
    const days = Math.max(1, Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
    if (days === 1) return "1D";
    return `${days}D`;
  } catch {
    return "";
  }
}

function fbFormatCta(cta: string): string {
  if (!cta) return "";
  return cta.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

function fbIsSnapshotUrl(url: string): boolean {
  return url?.includes("facebook.com/ads/archive/render_ad") || false;
}

function FbAdCreativeImage({ snapshotUrl, alt = "Ad creative", onImageExtracted }: { snapshotUrl: string; alt?: string; onImageExtracted?: (url: string) => void }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!snapshotUrl || lastUrlRef.current === snapshotUrl) return;
    lastUrlRef.current = snapshotUrl;
    setLoading(true);
    setFailed(false);
    setImageUrl(null);

    fetch(`/api/ads/extract-image?url=${encodeURIComponent(snapshotUrl)}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed");
        return res.json();
      })
      .then((data) => {
        setImageUrl(data.image_url);
        setLoading(false);
        if (data.image_url && onImageExtracted) onImageExtracted(data.image_url);
      })
      .catch(() => {
        setFailed(true);
        setLoading(false);
      });
  }, [snapshotUrl]);

  if (loading) {
    return (
      <div className="bg-gray-50 flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
      </div>
    );
  }

  if (failed || !imageUrl) return null;

  return (
    <div className="relative bg-gray-100">
      <img src={imageUrl} alt={alt} className="w-full" loading="lazy" referrerPolicy="no-referrer"
        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
      />
    </div>
  );
}

function FbVideoHoverCard({ videoUrl, thumbnailUrl, alt }: { videoUrl: string; thumbnailUrl?: string; alt: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHovering, setIsHovering] = useState(false);

  return (
    <div className="relative"
      onMouseEnter={() => { setIsHovering(true); videoRef.current?.play().catch(() => {}); }}
      onMouseLeave={() => { setIsHovering(false); if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; } }}
    >
      {!isHovering && (
        <>
          <img src={thumbnailUrl || videoUrl} alt={alt} className="w-full object-cover" loading="lazy" referrerPolicy="no-referrer"
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full bg-black/60 flex items-center justify-center">
              <Play className="h-5 w-5 text-white fill-white ml-0.5" />
            </div>
          </div>
        </>
      )}
      <video ref={videoRef} src={videoUrl} muted loop playsInline preload="none"
        className={`w-full object-cover ${isHovering ? "block" : "hidden"}`} crossOrigin="anonymous"
      />
    </div>
  );
}

function FbAdCard({ ad }: { ad: FbAdResult }) {
  const { toast } = useToast();
  const daysAgo = fbFormatDaysAgo(ad.ad_start_date_raw || ad.ad_start_date);
  const bodyText = ad.ad_creative_body?.replace(/\{\{[^}]+\}\}/g, "").trim() || "";
  const [expanded, setExpanded] = useState(false);
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(null);

  const isImageAd = ad.media_type !== "video";
  const directImageUrl = (ad.media_url && !fbIsSnapshotUrl(ad.media_url)) ? ad.media_url : null;
  const imageUrlForSave = directImageUrl || resolvedImageUrl;

  return (
    <div data-testid={`card-fb-ad-${ad.ad_id}`}
      className="mb-4 bg-white rounded-xl border border-[#E5E5E3] overflow-hidden hover:shadow-sm transition-all"
    >
      <div className="px-4 py-3 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full bg-[#F8F8F7] flex-shrink-0 overflow-hidden border border-[#E5E5E3]">
          {ad.page_logo_url ? (
            <img src={ad.page_logo_url} alt={ad.page_name} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-[10px] font-bold text-[#6B6B6B]">
              {ad.page_name?.charAt(0)}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[#1A1A1A] truncate">{ad.page_name}</h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {daysAgo && (
            <span className="flex items-center gap-1 text-xs text-[#8C8C8A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4CAF50]" />
              {daysAgo}
            </span>
          )}
          {ad.platform && (
            <span className="text-[10px] text-[#8C8C8A] uppercase">{ad.platform}</span>
          )}
        </div>
      </div>

      {bodyText && (
        <div className="px-4 pb-2">
          <p className="text-[13px] text-gray-700 leading-relaxed">
            {expanded ? bodyText : (bodyText.length > 180 ? bodyText.slice(0, 180) + "..." : bodyText)}
          </p>
          {bodyText.length > 180 && (
            <button className="text-xs font-medium text-gray-900 mt-1 hover:underline"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Show Less" : "Read More"}
            </button>
          )}
        </div>
      )}

      {ad.ad_creative_link_description && (
        <div className="px-4 pb-2">
          <p className="text-xs text-gray-500 leading-relaxed">
            {ad.ad_creative_link_description.length > 120
              ? ad.ad_creative_link_description.slice(0, 120) + "..."
              : ad.ad_creative_link_description}
          </p>
          {ad.cta_type && (
            <span className="inline-block mt-1.5 px-3 py-1 border border-gray-300 text-[11px] font-semibold text-gray-700 rounded">
              {fbFormatCta(ad.cta_type)}
            </span>
          )}
        </div>
      )}

      {(ad.media_url && !fbIsSnapshotUrl(ad.media_url)) ? (
        <div className="relative bg-gray-100">
          {ad.media_type === "video" ? (
            <FbVideoHoverCard videoUrl={ad.media_url} thumbnailUrl={ad.media_thumbnail_url} alt="Ad creative" />
          ) : (
            <img src={ad.media_url} alt="Ad creative" className="w-full" loading="lazy" referrerPolicy="no-referrer"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          )}
        </div>
      ) : ad.snapshot_url ? (
        <FbAdCreativeImage snapshotUrl={ad.snapshot_url} alt={`${ad.page_name} ad creative`}
          onImageExtracted={(url) => setResolvedImageUrl(url)}
        />
      ) : null}

      {isImageAd && imageUrlForSave && (
        <div className="px-4 py-2.5 border-t border-gray-50">
          <SaveTemplateBrandMenu source={{ imageUrl: imageUrlForSave }} testId={`button-save-template-${ad.ad_id}`} className="w-full h-8 text-xs" />
        </div>
      )}
    </div>
  );
}

interface CompetitorAd {
  id: number;
  imageUrl: string;
  imageWidth: number | null;
  imageHeight: number | null;
  aspectRatio: string | null;
  adStyle: string | null;
  adAngle: string | null;
  colorPalette: string[] | null;
  promptRecipe: string | null;
  niche: string | null;
  qualityScore: number | null;
  analysisStatus: string;
  textPlacement: string | null;
  textAmount: string | null;
  fontStyle: string | null;
  ctaStyle: string | null;
  backgroundTreatment: string | null;
  humanPresence: string | null;
  productVisibility: string | null;
  composition: string | null;
  createdAt: string;
}

const AD_STYLES_TAXONOMY = [
  "Problem-Solution", "Benefits", "Features", "Reviews", "Listicle",
  "Advertorial", "Us vs Them", "Education", "UGC Style", "Before & After",
  "Founder Story", "Social Proof", "Urgency", "Product Launch", "Sale",
  "How It Works", "Myth Busting", "Dark Mode",
];

export function FacebookAdsTab({ brandId, competitorProfileId }: { brandId: number; competitorProfileId: number }) {
  const queryClient = useQueryClient();
  const [adStyleFilter, setAdStyleFilter] = useState<string>("");
  const [sort, setSort] = useState<string>("newest");
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<string | null>(null);
  const scanStorageKey=`competitor-scan:${brandId}:${competitorProfileId}`;
  const [scanAction,setScanAction]=useState<{scope:string;id:number}|null>(null);
  const scanActionId=scanAction?.scope===scanStorageKey?scanAction.id:null;
  const setScanActionId=(id:number|null)=>setScanAction(id==null?null:{scope:scanStorageKey,id});
  const scanScope=useRef(scanStorageKey);
  const scanRequest=useRef<AbortController|null>(null);
  useEffect(()=>{
    scanScope.current=scanStorageKey;
    const saved=Number(sessionStorage.getItem(scanStorageKey));
    const actionId=Number.isSafeInteger(saved)&&saved>0?saved:null;
    setScanActionId(actionId);setScanning(!!actionId);setScanStatus(actionId?'Scanning… Saving ads and video media…':null);
    return()=>{scanRequest.current?.abort();};
  },[scanStorageKey]);
  const scanQuery=useQuery<{status:'running'|'completed'|'failed';error?:string;totalFound?:number;totalIngested?:number;totalAnalyzed?:number;totalFailed?:number;analysisFailed?:number}>({
    queryKey:[`/api/brands/${brandId}/competitors/${competitorProfileId}/scan-ads/${scanActionId}`],
    queryFn:async({signal})=>{
      const response=await fetch(`/api/brands/${brandId}/competitors/${competitorProfileId}/scan-ads/${scanActionId}`,{signal});
      if(!response.ok)throw new Error('Unable to check scan progress');
      return response.json();
    },
    enabled:scanActionId!=null,
    refetchInterval:query=>query.state.data?.status==='completed'||query.state.data?.status==='failed'?false:3000,
    retry:1,
  });
  useEffect(()=>{
    const state=scanQuery.data;
    if(!scanActionId)return;
    if(state?.status==='completed'||state?.status==='failed'){
      setScanning(false);
      setScanStatus(state.status==='failed'?`Error: ${state.error||'Scan failed'}`:`Done. Found ${state.totalFound??0} ads. Saved ${state.totalIngested??0}.${state.totalFailed?` ${state.totalFailed} could not be saved.`:""}${state.analysisFailed?` Analysis failed for ${state.analysisFailed}.`:""}`);
      sessionStorage.removeItem(scanStorageKey);
      setScanActionId(null);
      void queryClient.invalidateQueries({queryKey:[`/api/brands/${brandId}/competitors/${competitorProfileId}/ads`]});
    }else if(scanQuery.isError){setScanStatus('Scan is still being checked. Reconnecting…');}
    else if(state){setScanStatus('Scanning… Saving ads and video media, then analyzing…');}
  },[scanQuery.data,scanQuery.isError,scanActionId,scanStorageKey,brandId,competitorProfileId,queryClient]);
  const [selectedAd, setSelectedAd] = useState<CompetitorAd | null>(null);

  const params = new URLSearchParams();
  if (adStyleFilter) params.set("adStyle", adStyleFilter);
  params.set("sort", sort);

  const { data: ads = [], isLoading, isError, refetch } = useQuery<CompetitorAd[]>({
    queryKey: [`/api/brands/${brandId}/competitors/${competitorProfileId}/ads`, adStyleFilter, sort],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/competitors/${competitorProfileId}/ads?${params}`);
      if (!res.ok) throw new Error("Failed to load ads");
      return res.json();
    },
    enabled: !!brandId && !!competitorProfileId,
  });

  async function handleScan() {
    if(scanning)return;
    const scope=scanStorageKey;
    const controller=new AbortController();scanRequest.current=controller;
    setScanning(true);
    setScanStatus("Scanning… Fetching ads from Meta Ad Library…");
    try {
      const res = await fetch(`/api/brands/${brandId}/competitors/${competitorProfileId}/scan-ads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal:controller.signal,
      });
      if (!res.ok) throw new Error((await res.json()).message || "Scan failed");
      const data = await res.json();
      if(controller.signal.aborted||scanScope.current!==scope)return;
      if(res.status===202&&Number.isSafeInteger(data.actionId)){
        sessionStorage.setItem(scope,String(data.actionId));setScanActionId(data.actionId);
        setScanStatus('Scanning… Saving ads and video media…');
      }else{
        // Compatible with an old server during a rolling deployment.
        setScanStatus(`Done. ${data.totalIngested??0} ads ingested. Analyzing in the background…`);
        setScanning(false);
        void queryClient.invalidateQueries({queryKey:[`/api/brands/${brandId}/competitors/${competitorProfileId}/ads`]});
      }
    } catch (err: any) {
      if(controller.signal.aborted||scanScope.current!==scope)return;
      setScanStatus(`Error: ${err.message}`);setScanning(false);
    }
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 items-start">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-square bg-[#F8F8F7] rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select
          data-testid="select-competitor-ad-style"
          value={adStyleFilter}
          onChange={(e) => setAdStyleFilter(e.target.value)}
          className="text-xs border border-[#E5E5E3] rounded-md px-2 py-1.5 bg-white text-[#6B6B6B]"
        >
          <option value="">All Styles</option>
          {AD_STYLES_TAXONOMY.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          data-testid="select-competitor-ad-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="text-xs border border-[#E5E5E3] rounded-md px-2 py-1.5 bg-white text-[#6B6B6B]"
        >
          <option value="newest">Newest</option>
          <option value="quality">Quality Score</option>
        </select>
        <div className="flex-1" />
        {ads.length > 0 && (
          <span className="text-xs text-[#8C8C8A]">{ads.length} ad{ads.length !== 1 ? "s" : ""}</span>
        )}
        <Button
          data-testid="button-scan-competitor-ads"
          size="sm"
          variant="outline"
          className="border-[#E5E5E3] text-[#6B6B6B] hover:text-[#1A1A1A] hover:border-[#1A1A1A] text-xs"
          onClick={handleScan}
          disabled={scanning}
        >
          {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Scan Competitor Ads
        </Button>
      </div>

      {scanStatus && (
        <p data-testid="text-scan-status" className="text-xs text-[#6B6B6B] mb-4">{scanStatus}</p>
      )}

      {isError ? (
        <div role="alert" className="py-12 text-center text-sm text-muted-foreground">
          <p>Could not load saved competitor ads. This does not mean there are no ads.</p>
          <Button variant="outline" className="mt-3" onClick={() => void refetch()}>Retry loading</Button>
        </div>
      ) : ads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Search className="h-8 w-8 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">{adStyleFilter ? "No saved ads match this style" : "No ads saved for this competitor in this brand"}</p>
          {adStyleFilter ? <Button variant="outline" className="mt-3" onClick={() => setAdStyleFilter("")}>Show all styles</Button> : <p className="text-xs text-gray-500 mt-1">Use Scan Competitor Ads to fetch available ads from Meta Ad Library.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 items-start">
          {ads.map((ad) => (
            <div
              key={ad.id}
              data-testid={`card-competitor-ad-${ad.id}`}
              className="relative group cursor-pointer rounded-lg overflow-hidden border border-[#E5E5E3] bg-[#F8F8F7]"
              onClick={() => setSelectedAd(ad)}
            >
              <div className="relative">
              <img
                src={ad.imageUrl}
                alt={ad.adStyle || "Competitor ad"}
                className="block w-full h-auto object-contain"
                onError={(e) => { (e.target as HTMLImageElement).closest('.group')!.style.display = 'none'; }}
              />
              {ad.adStyle && (
                <div className="absolute bottom-2 left-2">
                  <span
                    data-testid={`badge-ad-style-${ad.id}`}
                    className="text-[10px] font-medium bg-black/70 text-white px-1.5 py-0.5 rounded backdrop-blur-sm"
                  >
                    {ad.adStyle}
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
              <div className="absolute top-2 right-2">
                <SaveTemplateBrandMenu source={{ imageUrl: ad.imageUrl }} iconOnly
                  testId={`button-save-competitor-brand-${ad.id}`} />
              </div>
              </div>
              <div className="border-t border-[#E5E5E3] bg-white px-3 py-1">
                <SaveTemplateBrandMenu source={{ imageUrl: ad.imageUrl }} label="Save as template"
                  testId={`button-save-competitor-template-${ad.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedAd && (
        <div
          data-testid="modal-competitor-ad-detail"
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedAd(null)}
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-[#E5E5E3] flex items-center justify-between">
              <h3 className="font-semibold text-[#1A1A1A]">Ad Detail</h3>
              <button
                data-testid="button-close-ad-modal"
                className="text-[#6B6B6B] hover:text-[#1A1A1A]"
                onClick={() => setSelectedAd(null)}
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <img
                src={selectedAd.imageUrl}
                alt={selectedAd.adStyle || "Ad"}
                className="w-full rounded-lg mb-4 object-contain max-h-80"
              />
              <div className="grid grid-cols-2 gap-3 text-xs text-[#6B6B6B] mb-4">
                {selectedAd.adStyle && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Style</span>
                    <p data-testid="text-modal-ad-style">{selectedAd.adStyle}</p>
                  </div>
                )}
                {selectedAd.adAngle && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Angle</span>
                    <p data-testid="text-modal-ad-angle">{selectedAd.adAngle}</p>
                  </div>
                )}
                {selectedAd.qualityScore != null && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Quality Score</span>
                    <p data-testid="text-modal-quality-score">{selectedAd.qualityScore}</p>
                  </div>
                )}
                {selectedAd.niche && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Niche</span>
                    <p>{selectedAd.niche}</p>
                  </div>
                )}
                {selectedAd.textPlacement && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Text Placement</span>
                    <p>{selectedAd.textPlacement}</p>
                  </div>
                )}
                {selectedAd.textAmount && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Text Amount</span>
                    <p>{selectedAd.textAmount}</p>
                  </div>
                )}
                {selectedAd.fontStyle && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Font Style</span>
                    <p>{selectedAd.fontStyle}</p>
                  </div>
                )}
                {selectedAd.ctaStyle && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">CTA Style</span>
                    <p>{selectedAd.ctaStyle}</p>
                  </div>
                )}
                {selectedAd.backgroundTreatment && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Background</span>
                    <p>{selectedAd.backgroundTreatment}</p>
                  </div>
                )}
                {selectedAd.humanPresence && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Human Presence</span>
                    <p>{selectedAd.humanPresence}</p>
                  </div>
                )}
                {selectedAd.productVisibility && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Product Visibility</span>
                    <p>{selectedAd.productVisibility}</p>
                  </div>
                )}
                {selectedAd.composition && (
                  <div>
                    <span className="font-medium text-[#1A1A1A]">Composition</span>
                    <p>{selectedAd.composition}</p>
                  </div>
                )}
              </div>
              {selectedAd.colorPalette && selectedAd.colorPalette.length > 0 && (
                <div className="mb-4">
                  <span className="text-xs font-medium text-[#1A1A1A]">Color Palette</span>
                  <div className="flex gap-1.5 mt-1.5">
                    {selectedAd.colorPalette.map((color, i) => (
                      <div
                        key={i}
                        data-testid={`swatch-color-${i}`}
                        className="h-6 w-6 rounded border border-[#E5E5E3]"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}
              {selectedAd.promptRecipe && (
                <div className="mb-4">
                  <span className="text-xs font-medium text-[#1A1A1A]">Prompt Recipe</span>
                  <p data-testid="text-modal-prompt-recipe" className="text-xs text-[#6B6B6B] mt-1">{selectedAd.promptRecipe}</p>
                </div>
              )}
              <SaveTemplateBrandMenu source={{ imageUrl: selectedAd.imageUrl }} label="Save as template"
                testId={`button-save-competitor-detail-template-${selectedAd.id}`} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function BoardDetailView({ boardId, onBack, initialCompetitorId }: { boardId: number; onBack?: () => void; initialCompetitorId?: number | null }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<number | null>(initialCompetitorId ?? null);
  const [activeTab, setActiveTab] = useState<"profile" | "sentiment" | "fbads">("profile");
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [websiteDialogOpen, setWebsiteDialogOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");

  const { data: board } = useQuery<CompetitorBoard>({
    queryKey: [`/api/competitor-boards/${boardId}`],
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && (data.status === "pending" || data.status === "analyzing") ? 5000 : false;
    },
  });

  const { data: brands = [] } = useQuery<Brand[]>({
    queryKey: ["/api/brands"],
  });

  const { data: competitors = [] } = useQuery<CompetitorProfile[]>({
    queryKey: [`/api/competitor-boards/${boardId}/competitors`],
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data || data.length === 0) return 3000;
      const allDone = data.every((c) => c.status === "completed" || c.status === "failed");
      return allDone ? false : 5000;
    },
  });

  const selected = competitors.find((c) => c.id === selectedCompetitorId) || competitors[0] || null;

  const { data: jobs = [] } = useQuery<ResearchJob[]>({
    queryKey: [`/api/competitors/${selected?.id}/jobs`],
    enabled: !!selected,
    refetchInterval: selected && selected.status !== "completed" && selected.status !== "failed" ? 5000 : false,
  });

  const brand = brands.find((b) => b.id === board?.brandId);

  const isAnalyzing = board?.status === "analyzing";

  const addWebsiteMutation = useMutation({
    mutationFn: async (url: string) => {
      const res = await fetch(`/api/competitor-boards/${boardId}/add-website`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      return res.json();
    },
    onSuccess: (profile: CompetitorProfile) => {
      queryClient.invalidateQueries({ queryKey: [`/api/competitor-boards/${boardId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/competitor-boards/${boardId}/competitors`] });
      setWebsiteDialogOpen(false);
      setWebsiteUrl("");
      setSelectedCompetitorId(profile.id);
      toast({ title: "Competitor added", description: "Website is being analyzed and researched." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (!board) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 rounded-full border-2 border-[#E5E5E3] border-t-[#8C8C8A] animate-spin" />
      </div>
    );
  }

  const hasNoCompetitors = competitors.length === 0 && !isAnalyzing;

  return (
    <div className="flex flex-col h-full bg-[#F8F8F7]">
      <div className="px-6 py-3 border-b border-[#E5E5E3] bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="button-back"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => onBack ? onBack() : setLocation("/competitor-analysis")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 data-testid="text-board-name" className="text-base font-semibold text-[#1A1A1A]">{board.name}</h1>
              <p className="text-xs text-[#6B6B6B]">
                Anchor brand: {brand?.name || "Loading..."} · {competitors.length} competitors
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DiscoverDialog
              boardId={boardId}
              open={discoverOpen}
              onOpenChange={setDiscoverOpen}
              trigger={
                <Button
                  data-testid="button-generate-more"
                  variant="outline"
                  size="sm"
                  disabled={isAnalyzing}
                >
                  <Sparkles className="h-4 w-4 mr-1.5" />
                  {competitors.length === 0 ? "Discover Competitors" : "Generate More"}
                </Button>
              }
            />
            <Dialog open={websiteDialogOpen} onOpenChange={setWebsiteDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="button-add-website"
                  variant="outline"
                  size="sm"
                  disabled={isAnalyzing}
                >
                  <Globe className="h-4 w-4 mr-1.5" />
                  Add Website
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Competitor Website</DialogTitle>
                  <DialogDescription>Enter a competitor's website URL to analyze and research them.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Competitor Website URL</label>
                    <Input
                      data-testid="input-website-url"
                      placeholder="e.g. https://competitor.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-1.5">We'll analyze the website to extract brand info, then run full research and sentiment analysis.</p>
                  </div>
                  <Button
                    data-testid="button-submit-website"
                    className="w-full bg-[#1A1A1A] hover:bg-[#2a2a2a] text-white"
                    disabled={!websiteUrl.trim() || addWebsiteMutation.isPending}
                    onClick={() => addWebsiteMutation.mutate(websiteUrl.trim())}
                  >
                    {addWebsiteMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing Website...
                      </>
                    ) : (
                      <>
                        <Globe className="h-4 w-4 mr-2" />
                        Add Competitor
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <StatusBadge status={board.status} />
          </div>
        </div>
      </div>

      {hasNoCompetitors ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="h-14 w-14 rounded-full bg-white border border-[#E5E5E3] flex items-center justify-center mb-4">
            <Crosshair className="h-6 w-6 text-[#8C8C8A]" />
          </div>
          <h3 className="text-lg font-medium text-[#1A1A1A]">No competitors yet</h3>
          <p className="text-sm text-[#6B6B6B] mt-1 max-w-md text-center mb-6">
            Click "Discover Competitors" to let AI find competitors, or "Add Website" to add one manually.
          </p>
          <div className="flex gap-3">
            <Button
              data-testid="button-discover-empty"
              className="bg-[#1A1A1A] hover:bg-[#2a2a2a] text-white"
              onClick={() => setDiscoverOpen(true)}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Discover Competitors
            </Button>
            <Button
              data-testid="button-add-website-empty"
              variant="outline"
              className="border-[#E5E5E3] text-[#6B6B6B] hover:text-[#1A1A1A] hover:border-[#1A1A1A]"
              onClick={() => setWebsiteDialogOpen(true)}
            >
              <Globe className="h-4 w-4 mr-2" />
              Add Website
            </Button>
          </div>
        </div>
      ) : competitors.length === 0 && isAnalyzing ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="h-10 w-10 rounded-full border-2 border-[#E5E5E3] border-t-[#8C8C8A] animate-spin mb-4" />
          <h3 className="text-lg font-medium text-[#1A1A1A]">Analyzing competitors...</h3>
          <p className="text-sm text-[#8C8C8A] mt-1">Full research and sentiment analysis is running</p>
        </div>
      ) : (
        <>
          <div className="px-6 py-3 border-b border-[#E5E5E3] bg-white flex items-center gap-2 overflow-x-auto">
            <span className="text-xs text-[#8C8C8A] font-medium shrink-0 mr-1">Competitors:</span>
            {competitors.map((comp) => {
              const isSelected = selected?.id === comp.id;
              return (
                <button
                  key={comp.id}
                  data-testid={`tab-competitor-${comp.id}`}
                  onClick={() => {
                    setSelectedCompetitorId(comp.id);
                    setActiveTab("profile");
                  }}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors shrink-0 border ${
                    isSelected
                      ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                      : "bg-white text-[#1A1A1A] border-[#E5E5E3] hover:bg-[#F8F8F7] hover:border-[#1A1A1A]"
                  }`}
                >
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${
                    isSelected ? "bg-white/20 text-white" : "bg-[#F0EFED] text-[#6B6B6B]"
                  }`}>
                    {comp.brandName.charAt(0)}
                  </span>
                  {comp.brandName}
                </button>
              );
            })}
          </div>

          {selected && (
            <div className="flex-1 overflow-auto">
              <div className="px-6 py-5 bg-white border-b border-[#E5E5E3]">
                <div className="flex items-center gap-4 mb-3">
                  <div className="h-12 w-12 rounded-full bg-[#F8F8F7] border border-[#E5E5E3] flex items-center justify-center text-[#6B6B6B] font-bold text-lg">
                    {selected.brandName.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h2 className="brand-detail-title">{selected.brandName}</h2>
                      <StatusBadge status={selected.status} />
                    </div>
                    {selected.positioning && (
                      <p className="text-sm text-[#6B6B6B] mt-0.5 line-clamp-1">{selected.positioning}</p>
                    )}
                  </div>
                </div>
                {jobs.length > 0 && <SourceIndicator jobs={jobs} />}
              </div>

              <div className="px-6 border-b border-[#E5E5E3] bg-white">
                <div className="flex gap-0">
                  {([
                    { key: "profile" as const, label: "Profile", icon: <Target className="h-3.5 w-3.5" /> },
                    { key: "sentiment" as const, label: "Sentiment", icon: <MessageSquare className="h-3.5 w-3.5" /> },
                    { key: "fbads" as const, label: "Facebook Ads", icon: <Facebook className="h-3.5 w-3.5" /> },
                  ]).map((tab) => (
                    <button
                      key={tab.key}
                      data-testid={`tab-${tab.key}`}
                      onClick={() => setActiveTab(tab.key)}
                      className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab.key
                          ? "border-[#1A1A1A] text-[#1A1A1A]"
                          : "border-transparent text-[#6B6B6B] hover:text-[#1A1A1A] hover:border-[#E5E5E3]"
                      }`}
                    >
                      {tab.icon} {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`p-6 ${activeTab === "fbads" ? "" : "max-w-4xl"}`}>
                {activeTab === "fbads" ? (
                  <FacebookAdsTab brandId={board?.brandId ?? 0} competitorProfileId={selected.id} />
                ) : selected.status === "pending" ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="h-8 w-8 rounded-full border-2 border-[#E5E5E3] border-t-[#8C8C8A] animate-spin mb-3" />
                    <p className="text-sm text-[#8C8C8A]">Analyzing this competitor...</p>
                  </div>
                ) : (
                  <>
                    {activeTab === "profile" && <ProfileTab profile={selected} />}
                    {activeTab === "sentiment" && <SentimentTab profileId={selected.id} status={selected.status} />}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function CompetitorProfileDetailView({ profile, onBack, brandId, initialTab = "profile" }: { profile: CompetitorProfile; onBack?: () => void; brandId?: number; initialTab?: "profile" | "fbads" }) {
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "sentiment" | "fbads">(initialTab);

  const { data: jobs = [] } = useQuery<ResearchJob[]>({
    queryKey: [`/api/competitors/${profile.id}/jobs`],
    enabled: !!profile.id,
    refetchInterval: profile.status !== "completed" && profile.status !== "failed" ? 5000 : false,
  });

  return (
    <div className="flex flex-col">
      {editing && <CompetitorProfileEditor profile={profile} onClose={() => setEditing(false)} />}
      <div className="flex justify-end"><Button variant="outline" onClick={() => setEditing(true)}>Edit details</Button></div>
      <div className="pb-4 mb-2">
        {onBack && (
          <Button
            data-testid="button-back-profile"
            variant="ghost"
            size="sm"
            className="-ml-1 text-[#6B6B6B] hover:text-[#1A1A1A]"
            onClick={onBack}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to competitors
          </Button>
        )}
      </div>
      <div className="bg-white rounded-xl border border-[#E5E5E3] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#E5E5E3] bg-[#F8F8F7]">
          <div className="flex items-center gap-4 mb-3">
            <div className="h-12 w-12 rounded-full bg-white border border-[#E5E5E3] flex items-center justify-center overflow-hidden shrink-0">
              <img
                src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(profile.brandName.toLowerCase().replace(/\s+/g, '') + '.com')}&sz=64`}
                alt={profile.brandName}
                className="h-full w-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                  (e.target as HTMLImageElement).parentElement!.textContent = profile.brandName.charAt(0);
                  (e.target as HTMLImageElement).parentElement!.className = "h-12 w-12 rounded-full bg-white border border-[#E5E5E3] flex items-center justify-center text-lg font-bold text-[#6B6B6B] shrink-0";
                }}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 data-testid="text-profile-detail-name" className="brand-detail-title">{profile.brandName}</h2>
                {(profile.status === "analyzing" || profile.status === "processing" || profile.status === "pending") ? (
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#8C8C8A]">
                    <div className="h-3 w-3 rounded-full border border-[#E5E5E3] border-t-[#8C8C8A] animate-spin" />
                    Analyzing...
                  </span>
                ) : (
                  <StatusBadge status={profile.status} />
                )}
              </div>
              {profile.positioning && (
                <p className="text-sm text-[#6B6B6B] mt-0.5 line-clamp-1">{profile.positioning}</p>
              )}
            </div>
          </div>
          {jobs.length > 0 && <SourceIndicator jobs={jobs} />}
        </div>
        <div className="px-6 border-b border-[#E5E5E3] bg-white">
          <div className="flex gap-0">
            {([
              { key: "profile" as const, label: "Profile", icon: <Target className="h-3.5 w-3.5" /> },
              { key: "sentiment" as const, label: "Sentiment", icon: <MessageSquare className="h-3.5 w-3.5" /> },
              { key: "fbads" as const, label: "Facebook Ads", icon: <Facebook className="h-3.5 w-3.5" /> },
            ]).map((tab) => (
              <button
                key={tab.key}
                data-testid={`tab-profile-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-[#1A1A1A] text-[#1A1A1A]"
                    : "border-transparent text-[#6B6B6B] hover:text-[#1A1A1A] hover:border-[#E5E5E3]"
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className={`p-6 bg-white ${activeTab === "fbads" ? "" : "max-w-4xl"}`}>
          {activeTab === "fbads" ? (
            <FacebookAdsTab brandId={brandId ?? 0} competitorProfileId={profile.id} />
          ) : profile.status === "pending" || profile.status === "analyzing" || profile.status === "processing" ? (
            <>
              {activeTab === "profile" && <ProfileTabSkeleton />}
              {activeTab === "sentiment" && <SentimentTabSkeleton />}
            </>
          ) : (
            <>
              {activeTab === "profile" && <ProfileTab profile={profile} />}
              {activeTab === "sentiment" && <SentimentTab profileId={profile.id} status={profile.status} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function BrandCompetitors({ brandId }: { brandId: number }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [selected, setSelected] = useState<{ id: number; tab: 'profile' | 'fbads' } | null>(null);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState('');
  const [discoveryBoard, setDiscoveryBoard] = useState<number | null>(null);
  const { data: boards = [], isLoading, isError } = useQuery<CompetitorBoard[]>({
    queryKey: ['/api/competitor-boards', { brandId }],
    queryFn: async () => { const r = await fetch(`/api/competitor-boards?brandId=${brandId}`); if (!r.ok) throw new Error('Could not load competitors'); return r.json(); },
  });
  const profileQueries = useQueries({ queries: boards.map(board => ({
    queryKey: [`/api/competitor-boards/${board.id}/competitors`],
    queryFn: async (): Promise<CompetitorProfile[]> => { const r = await fetch(`/api/competitor-boards/${board.id}/competitors`); if (!r.ok) throw new Error('Could not load competitors'); return r.json(); },
    refetchInterval: (q: { state: { data?: CompetitorProfile[] } }) => q.state.data?.some(p => !['completed', 'failed'].includes(p.status)) || ['pending', 'analyzing'].includes(board.status) ? 5000 : false as const,
  })) });
  const profiles = flattenBrandCompetitors(profileQueries.map(query => query.data));
  const active = profiles.find(p => p.id === selected?.id);
  const destination = useChatPlatformNavigation(state => state.target);
  const loadingProfiles = isLoading || profileQueries.some(query => query.isLoading);
  useEffect(() => {
    if (destination?.brandId !== brandId || destination.competitorId == null || loadingProfiles) return;
    if (profiles.some(profile => profile.id === destination.competitorId)) setSelected({ id: destination.competitorId, tab: 'profile' });
    else toast({ title: 'Competitor unavailable', variant: 'destructive' });
    useChatPlatformNavigation.setState({ target: null });
  }, [destination, brandId, loadingProfiles, profiles, toast]);

  const ensureBoard = async () => {
    if (boards[0]) return boards[0].id;
    const r = await fetch('/api/competitor-boards', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ brandId, name: 'Competitors' }) });
    if (!r.ok) throw new Error('Could not prepare competitor research');
    const board = await r.json() as CompetitorBoard;
    qc.setQueryData(['/api/competitor-boards', { brandId }], [board]);
    return board.id;
  };
  const add = useMutation({
    mutationFn: async () => {
      const boardId = await ensureBoard();
      const r = await fetch(`/api/competitor-boards/${boardId}/add-website`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }) });
      if (!r.ok) throw new Error((await r.json()).message || 'Could not scan competitor');
      await qc.invalidateQueries({ queryKey: [`/api/competitor-boards/${boardId}/competitors`] });
    },
    onSuccess: () => { setAdding(false); setUrl(''); toast({ title: 'Competitor scan started' }); },
    onError: (e: Error) => toast({ title: 'Could not scan competitor', description: e.message, variant: 'destructive' }),
  });
  const discover = useMutation({ mutationFn: ensureBoard, onSuccess: setDiscoveryBoard, onError: (e: Error) => toast({ title: e.message, variant: 'destructive' }) });
  if (active) return <div><CompetitorProfileDetailView key={`${active.id}/${selected?.tab}`} profile={active} brandId={brandId} initialTab={selected?.tab} onBack={() => setSelected(null)} /></div>;
  return <div className="space-y-5" data-testid="brand-competitors">
    <div className="flex flex-wrap items-center justify-between gap-4"><div><h2 className="text-[15px] font-semibold text-[#1A1A1A]">Competitors</h2><p className="text-sm text-muted-foreground">Research, positioning, and ads for each competitor.</p></div><div className="flex flex-wrap gap-2"><Button variant="outline" disabled={discover.isPending || isLoading || isError} onClick={() => discover.mutate()}>Discover competitors</Button><Button disabled={isLoading || isError} onClick={() => setAdding(true)}>Add competitor</Button></div></div>
    {(isLoading || profileQueries.some(q => q.isLoading)) && <p role="status">Loading competitors…</p>}
    {(isError || profileQueries.some(q => q.isError)) && <p role="alert">Some competitors could not be loaded. <button className="underline" onClick={() => { void qc.invalidateQueries({ queryKey: ['/api/competitor-boards'] }); profileQueries.forEach(q => void q.refetch()); }}>Try again</button></p>}
    {!isLoading && !profiles.length && !profileQueries.some(q => q.isLoading || q.isError) && <p className="rounded-xl border p-8 text-muted-foreground">No competitors yet. Add a website or discover competitors for this brand.</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{profiles.map(profile => <Card key={profile.id} data-testid={`competitor-card-${profile.id}`}><CardContent className="p-5 space-y-3"><div className="flex justify-between gap-3"><h3 className="font-semibold text-[15px]">{profile.brandName}</h3><StatusBadge status={profile.status}/></div><p className="text-sm text-muted-foreground line-clamp-3">{profile.positioning || profile.description || 'Research is being prepared.'}</p><div className="flex gap-2"><Button variant="outline" onClick={() => setSelected({ id: profile.id, tab: 'profile' })}>View details</Button><Button variant="outline" onClick={() => setSelected({ id: profile.id, tab: 'fbads' })}>View ads</Button></div></CardContent></Card>)}</div>
    <Dialog open={adding} onOpenChange={setAdding}><DialogContent><DialogHeader><DialogTitle>Add competitor</DialogTitle><DialogDescription>Scan a competitor website to research its brand and positioning.</DialogDescription></DialogHeader><form onSubmit={e => { e.preventDefault(); add.mutate(); }} className="space-y-4"><label className="block text-sm">Competitor website<Input type="url" required value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com" /></label><Button type="submit" disabled={add.isPending}>{add.isPending ? 'Starting scan…' : 'Scan competitor'}</Button></form></DialogContent></Dialog>
    {discoveryBoard != null && <DiscoverDialog boardId={discoveryBoard} open onOpenChange={open => { if (!open) setDiscoveryBoard(null); }} trigger={<span/>}/>}
  </div>;
}

export function CompetitorAnalysisContent({
  initialBoardId,
  onBoardChange,
  hideHeader,
  brandId,
}: {
  initialBoardId?: number | null;
  onBoardChange?: (boardId: number | null) => void;
  hideHeader?: boolean;
  brandId?: number;
}) {
  const [activeBoardId, setActiveBoardId] = useState<number | null>(initialBoardId ?? null);

  useEffect(() => {
    setActiveBoardId(initialBoardId ?? null);
  }, [initialBoardId]);

  const handleSelectBoard = (id: number) => {
    setActiveBoardId(id);
    onBoardChange?.(id);
  };

  const handleBack = () => {
    setActiveBoardId(null);
    onBoardChange?.(null);
  };

  if (brandId != null) return <BrandCompetitors key={brandId} brandId={brandId} />;

  return activeBoardId
    ? <BoardDetailView boardId={activeBoardId} onBack={handleBack} />
    : <BoardsListing onSelectBoard={handleSelectBoard} hideHeader={hideHeader} brandId={brandId} />;
}

export default function CompetitorAnalysis() {
  const params = useParams<{ boardId?: string }>();
  const boardId = params.boardId ? parseInt(params.boardId) : null;

  return (
    <Layout>
      {boardId ? <BoardDetailView boardId={boardId} /> : <BoardsListing />}
    </Layout>
  );
}
